import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react'; // Añadir useCallback
import Cookies from 'js-cookie';
import Logger from '../Services/loggerService';

// 1. Crear el Contexto
const UserContext = createContext(null);

// 2. Crear el Proveedor del Contexto
export const UserProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [whatsappNumber, setWhatsappNumber] = useState(null);
    const [loading, setLoading] = useState(true);

    // Función segura para decodificar JWT manejando Base64Url y UTF-8
    const decodeJWT = (token) => {
        try {
            if (!token || typeof token !== 'string') return null;
            const base64Url = token.split('.')[1];
            if (!base64Url) return null;
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error('[decodeJWT] Error decodificando token:', e);
            return null;
        }
    };

    // Efecto para cargar datos iniciales desde sessionStorage o verificar token
    useEffect(() => {
        console.log("[UserContext] 🔍 Iniciando carga inicial...");
        try {
            const sessionData = sessionStorage.getItem('userSession');
            const token = Cookies.get('token');
            console.log("[UserContext] Sesión en storage:", sessionData ? "Sí" : "No");
            console.log("[UserContext] Token en cookies:", token ? "Sí" : "No");

            if (sessionData || token) {
                let parsedData = null;
                
                if (sessionData) {
                    try {
                        parsedData = JSON.parse(sessionData);
                        console.log("[UserContext] Datos de sesión recuperados:", parsedData.nit);
                    } catch (e) {
                        sessionStorage.removeItem('userSession');
                    }
                }

                // Si tenemos token, intentamos enriquecer o cargar desde ahí
                if (token) {
                    const payload = decodeJWT(token);
                    if (payload) {
                        console.log("[UserContext] ✅ Token decodificado correctamente:", {
                            nit: payload.nit,
                            razonSocial: payload.razonSocial ? "Ok" : "Falta",
                            firstName: payload.firstName ? "Ok" : "Falta"
                        });

                        const isExpired = payload.exp * 1000 < Date.now();
                        
                        if (!isExpired) {
                            // Si faltan campos básicos, forzamos enriquecimiento
                            const needsEnrichment = !parsedData || !parsedData.firstName || !parsedData.razonSocial;
                            
                            if (needsEnrichment) {
                                console.log("[UserContext] 💡 Enriqueciendo sesión con datos frescos del token...");
                                parsedData = {
                                    ...(parsedData || {}),
                                    nit: payload.nit || (parsedData ? parsedData.nit : Cookies.get('nit')),
                                    role: payload.role || (parsedData ? parsedData.role : Cookies.get('userRole')),
                                    razonSocial: payload.razonSocial || (parsedData ? parsedData.razon_social : null),
                                    firstName: payload.firstName || null,
                                    lastName: payload.lastName || null,
                                    email: payload.email || (parsedData ? parsedData.email : null),
                                    timestamp: Date.now()
                                };
                                
                                console.log("[UserContext] 💾 Guardando sesión actualizada:", parsedData.razonSocial);
                                sessionStorage.setItem('userSession', JSON.stringify(parsedData));
                            }
                        } else {
                            console.warn("[UserContext] ⚠️ El token ha expirado");
                        }
                    }
                }

                // Aplicar datos finales
                if (parsedData) {
                    setUserData(parsedData);
                }
            }
        } catch (error) {
            console.error('[UserContext] ❌ Error crítico en carga inicial:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Función para extender la sesión (ejemplo, podría estar en otro lugar)
    const extendSession = async () => {
        // Lógica para llamar al backend y extender la sesión
        console.log("[UserContext] extendSession llamado (implementación pendiente)");
        // Debería llamar a un servicio que haga la petición al backend
        // await authService.refreshToken();
    };

    // *** AÑADIR LA FUNCIÓN LOGOUTUSER ***
    const logoutUser = useCallback(() => {
        console.log("[UserContext] logoutUser llamado. Limpiando datos de usuario.");
        setUserData(null); // Limpiar el estado local
        sessionStorage.removeItem('userSession'); // Limpiar la sesión guardada
        // La limpieza de Cookies y la navegación se manejan en handleLogout (NavBar.jsx)
    }, []); // No tiene dependencias externas directas aquí

    // Memoize the context value object
    const contextValue = useMemo(() => {
        return {
            userData,
            setUserData,
            loading,
            extendSession,
            logoutUser,
            whatsappNumber,
            setWhatsappNumber
        };
    }, [userData, loading, logoutUser, whatsappNumber]);

    // Listener para eventos de autenticación (loginSuccess, userAuthenticated)
    useEffect(() => {
        const handleUserAuthenticated = (event) => {
            const userInfo = event.detail;
            if (userInfo && (userInfo.nit || userInfo.role)) {
                // Preservar todos los datos recibidos (firstName, phone, etc.)
                const updatedUserData = {
                    ...userInfo,
                    // Asegurar mapeos consistentes
                    razonSocial: userInfo.razonSocial || userInfo.razon_social || userInfo.userRazonSocial || null,
                    firstName: userInfo.firstName || null,
                    lastName: userInfo.lastName || null,
                    email: userInfo.email || userInfo.userEmail || null,
                    imageUrl: userInfo.imageUrl || userInfo.profilePicture || userInfo.image_url || null,
                    timestamp: Date.now()
                };
                console.log("[UserContext] Actualizando datos por evento:", updatedUserData.razonSocial);
                setUserData(updatedUserData);
                // También actualizar sessionStorage
                sessionStorage.setItem('userSession', JSON.stringify(updatedUserData));
            }
        };

        // Escuchar eventos de autenticación
        window.addEventListener('loginSuccess', handleUserAuthenticated);
        window.addEventListener('userAuthenticated', handleUserAuthenticated);

        return () => {
            window.removeEventListener('loginSuccess', handleUserAuthenticated);
            window.removeEventListener('userAuthenticated', handleUserAuthenticated);
        };
    }, []);

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

// 4. Crear el Hook personalizado para usar el contexto
export const useUser = () => {
    const context = useContext(UserContext);
    if (context === null) {
        // Error si se usa fuera del proveedor
        throw new Error('useUser debe usarse dentro de un UserProvider');
    }
    return context;
};
